import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeftIcon,
    SparklesIcon,
    ChartBarIcon,
    ArrowPathIcon,
} from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import apiService from '../services/api';
import { sentimentStyle, impactStyle, marketSentimentStyle } from '../utils/styleHelpers';

const STYLE_COLORS = {
    zhihu: { bg: 'rgba(99,102,241,0.08)', color: '#6366f1', label: '知乎风格' },
    industry: { bg: 'rgba(52,199,89,0.08)', color: '#34C759', label: '行业分析' },
    debate: { bg: 'rgba(255,59,48,0.08)', color: '#FF3B30', label: '观点辩论' },
    thesis: { bg: 'rgba(88,86,214,0.08)', color: '#5856D6', label: '深度论文' },
    viral: { bg: 'rgba(255,159,10,0.08)', color: '#FF9F0A', label: '热点传播' },
    professional: { bg: 'rgba(0,122,255,0.08)', color: '#007AFF', label: '专业报告' },
    casual: { bg: 'rgba(175,82,222,0.08)', color: '#AF52DE', label: '轻松阅读' },
    short: { bg: 'rgba(90,200,250,0.08)', color: '#5AC8FA', label: '快讯' },
};

// ======== WriteRead-style markdown components ========
const readerComponents = {
    h1: ({ children }) => (
        <h1 style={{
            fontSize: '2rem', fontWeight: '700', letterSpacing: '-0.5px',
            marginBottom: '16px', lineHeight: '1.2', color: '#111827',
        }}>{children}</h1>
    ),
    h2: ({ children }) => (
        <h2 style={{
            fontSize: '1.5rem', fontWeight: '600', marginTop: '48px', marginBottom: '16px',
            paddingBottom: '8px', borderBottom: '1px solid #e5e7eb', color: '#111827',
        }}>{children}</h2>
    ),
    h3: ({ children }) => (
        <h3 style={{
            fontSize: '1.2rem', fontWeight: '600', marginTop: '32px',
            marginBottom: '12px', color: '#111827',
        }}>{children}</h3>
    ),
    p: ({ children }) => (
        <p style={{ marginBottom: '16px', color: '#4b5563', lineHeight: '1.8' }}>{children}</p>
    ),
    a: ({ href, children }) => (
        <a href={href} target="_blank" rel="noopener noreferrer"
           style={{ color: '#6366f1', textDecoration: 'none' }}
           onMouseEnter={e => { e.target.style.textDecoration = 'underline'; }}
           onMouseLeave={e => { e.target.style.textDecoration = 'none'; }}
        >{children}</a>
    ),
    blockquote: ({ children }) => (
        <blockquote style={{
            borderLeft: '4px solid #6366f1', paddingLeft: '20px',
            margin: '24px 0', color: '#9ca3af', fontStyle: 'italic',
        }}>{children}</blockquote>
    ),
    ul: ({ children }) => (
        <ul style={{ marginBottom: '16px', paddingLeft: '24px', color: '#4b5563' }}>{children}</ul>
    ),
    ol: ({ children }) => (
        <ol style={{ marginBottom: '16px', paddingLeft: '24px', color: '#4b5563' }}>{children}</ol>
    ),
    li: ({ children }) => (
        <li style={{ marginBottom: '8px', lineHeight: '1.7' }}>{children}</li>
    ),
    strong: ({ children }) => (
        <strong style={{ fontWeight: '600', color: '#111827' }}>{children}</strong>
    ),
    hr: () => (
        <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '48px 0' }} />
    ),
    code: ({ inline, children, className }) => {
        if (inline) {
            return <code style={{
                background: '#f3f4f6', padding: '2px 6px', borderRadius: '4px',
                fontSize: '0.875em', fontFamily: "'SF Mono', Monaco, 'Cascadia Code', monospace",
            }}>{children}</code>;
        }
        return (
            <pre style={{
                background: '#1f2937', borderRadius: '8px', padding: '16px 20px',
                overflowX: 'auto', marginBottom: '24px',
            }}>
                <code style={{
                    background: 'transparent', padding: 0, color: '#e5e7eb',
                    fontSize: '0.875rem', lineHeight: '1.7',
                    fontFamily: "'SF Mono', Monaco, 'Cascadia Code', monospace",
                }}>{children}</code>
            </pre>
        );
    },
    table: ({ children }) => (
        <div style={{ overflowX: 'auto', margin: '24px 0' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>{children}</table>
        </div>
    ),
    th: ({ children }) => (
        <th style={{
            border: '1px solid #e5e7eb', padding: '12px', textAlign: 'left',
            background: '#f9fafb', fontWeight: '600', color: '#111827',
        }}>{children}</th>
    ),
    td: ({ children }) => (
        <td style={{
            border: '1px solid #e5e7eb', padding: '12px', textAlign: 'left', color: '#4b5563',
        }}>{children}</td>
    ),
    img: ({ src, alt }) => (
        <img src={src} alt={alt || ''} style={{ maxWidth: '100%', borderRadius: '8px', margin: '24px 0' }} />
    ),
};

// ======== Extract TOC headings from markdown ========
function extractHeadings(markdown) {
    const headings = [];
    const lines = markdown.split('\n');
    for (const line of lines) {
        const m = line.match(/^(#{2,3})\s+(.+)/);
        if (m) {
            const level = m[1].length;
            const text = m[2].replace(/[#*_`\[\]]/g, '').trim();
            const id = text.replace(/\s+/g, '-').replace(/[^\w\u4e00-\u9fff-]/g, '').toLowerCase();
            headings.push({ level, text, id });
        }
    }
    return headings;
}

// Override h2/h3 to add id for TOC linking
const readerComponentsWithIds = {
    ...readerComponents,
    h2: ({ children }) => {
        const text = typeof children === 'string' ? children : (Array.isArray(children) ? children.map(c => typeof c === 'string' ? c : '').join('') : '');
        const id = text.replace(/\s+/g, '-').replace(/[^\w\u4e00-\u9fff-]/g, '').toLowerCase();
        return (
            <h2 id={id} style={{
                fontSize: '1.5rem', fontWeight: '600', marginTop: '48px', marginBottom: '16px',
                paddingBottom: '8px', borderBottom: '1px solid #e5e7eb', color: '#111827',
            }}>{children}</h2>
        );
    },
    h3: ({ children }) => {
        const text = typeof children === 'string' ? children : (Array.isArray(children) ? children.map(c => typeof c === 'string' ? c : '').join('') : '');
        const id = text.replace(/\s+/g, '-').replace(/[^\w\u4e00-\u9fff-]/g, '').toLowerCase();
        return (
            <h3 id={id} style={{
                fontSize: '1.2rem', fontWeight: '600', marginTop: '32px',
                marginBottom: '12px', color: '#111827',
            }}>{children}</h3>
        );
    },
};

const card = {
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderRadius: '20px',
    border: '1px solid rgba(255, 255, 255, 0.5)',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.04)',
};

// ======== Translate button (shared) ========
const TranslateButton = ({ translating, showZh, onClick }) => (
    <button
        onClick={onClick}
        disabled={translating}
        style={{
            marginLeft: 'auto',
            display: 'inline-flex', alignItems: 'center', gap: '5px',
            padding: '5px 14px', borderRadius: '8px',
            fontSize: '0.8rem', fontWeight: '600', cursor: translating ? 'wait' : 'pointer',
            border: '1px solid rgba(0,0,0,0.08)',
            background: showZh ? 'rgba(99,102,241,0.08)' : 'rgba(0,0,0,0.03)',
            color: showZh ? '#6366f1' : '#555',
            transition: 'all 0.2s',
        }}
    >
        {translating ? (
            <><ArrowPathIcon style={{ width: '14px', height: '14px', animation: 'spin 1s linear infinite' }} /> 翻译中…</>
        ) : showZh ? '查看原文' : '翻译全文'}
    </button>
);

// ======== Unified content view for feed & ai-event ========
const ContentView = ({ feed, setFeed, goBack, isAIEvent }) => {
    const [translating, setTranslating] = useState(false);
    const [showZh, setShowZh] = useState(!!feed.content_zh);

    const tags = feed.ai_tags || feed.tags || [];
    const origContent = feed.content || feed.summary || '';
    const isHtml = !isAIEvent && origContent.includes('<');
    const displayContent = showZh && feed.content_zh ? feed.content_zh : origContent;

    const source = feed.feed_source || feed.source || '';
    const date = feed.published_at || feed.date || feed.created_at;

    const marketImpact = !isAIEvent ? feed.market_impact : null;
    const keyMetrics = !isAIEvent ? feed.key_metrics : null;
    const hasMetrics = (m) => m && (m.revenue || m.eps || m.stock_change || m.guidance || (m.key_numbers && m.key_numbers.length > 0));
    const hasSidebar = (marketImpact?.impact_level) || hasMetrics(keyMetrics);

    const badge = (text, style) => (
        <span style={{
            display: 'inline-block', padding: '3px 10px', borderRadius: '8px',
            fontSize: '0.75rem', fontWeight: '600', ...style,
        }}>{text}</span>
    );

    const handleTranslate = async () => {
        if (feed.content_zh) {
            setShowZh(v => !v);
            return;
        }
        setTranslating(true);
        try {
            const res = isAIEvent
                ? await apiService.translateAIEvent(feed.id)
                : await apiService.translateFeed(feed.id);
            const updated = res?.data || res;
            if (updated.content_zh) {
                setFeed(prev => ({ ...prev, content_zh: updated.content_zh }));
                setShowZh(true);
            }
        } catch (e) {
            console.error('Translation failed:', e);
        } finally {
            setTranslating(false);
        }
    };

    const mainContent = (
        <>
            {/* Title */}
            <h1 style={{
                fontSize: '1.8rem', fontWeight: '700', letterSpacing: '-0.5px',
                lineHeight: '1.3', color: '#111827', margin: '0 0 12px',
            }}>
                {feed.title_zh || feed.title}
            </h1>

            {feed.title_zh && feed.title && (
                <div style={{ fontSize: '1rem', color: '#6b7280', marginBottom: '16px', lineHeight: '1.5' }}>
                    {feed.title}
                </div>
            )}

            {/* Meta line */}
            <div style={{
                display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
                paddingBottom: '20px', marginBottom: '24px',
                borderBottom: '1px solid #e5e7eb',
            }}>
                {source && (
                    <span style={{
                        fontSize: '0.8rem', fontWeight: '600', padding: '3px 12px',
                        borderRadius: '6px', background: 'rgba(88,86,214,0.08)', color: '#5856D6',
                    }}>{source}</span>
                )}
                {feed.author && (
                    <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>By {feed.author}</span>
                )}
                {feed.issue && (
                    <span style={{ fontSize: '0.85rem', color: '#6b7280', fontWeight: '500' }}>{feed.issue}</span>
                )}
                {date && (
                    <span style={{ fontSize: '0.85rem', color: '#9ca3af' }}>
                        {new Date(date).toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </span>
                )}
                {typeof feed.category === 'string' && feed.category && (
                    <span style={{
                        padding: '2px 8px', borderRadius: '6px', fontSize: '0.72rem',
                        fontWeight: '600', textTransform: 'uppercase',
                        background: 'rgba(0,0,0,0.04)', color: '#86868B',
                    }}>{feed.category}</span>
                )}
                {typeof feed.sentiment?.label === 'string' && badge(feed.sentiment.label, sentimentStyle(feed.sentiment.label))}
                <TranslateButton translating={translating} showZh={showZh} onClick={handleTranslate} />
            </div>

            {/* Tags */}
            {tags.length > 0 && (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '24px', marginTop: '-8px' }}>
                    {tags.map((tag, i) => (
                        <span key={i} style={{
                            fontSize: '0.75rem', fontWeight: '600', padding: '3px 10px',
                            borderRadius: '6px', background: 'rgba(88,86,214,0.08)', color: '#5856D6',
                        }}>{typeof tag === 'string' ? tag : ''}</span>
                    ))}
                </div>
            )}

            {/* AI Summary */}
            {feed.ai_summary && !showZh && (
                <div style={{ marginBottom: '24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                        <SparklesIcon style={{ width: '16px', height: '16px', color: '#6366f1' }} />
                        <span style={{ fontSize: '0.82rem', fontWeight: '700', color: '#6366f1' }}>AI 摘要</span>
                    </div>
                    <div style={{
                        background: 'rgba(99,102,241,0.05)',
                        borderLeft: '3px solid #6366f1',
                        borderRadius: '0 12px 12px 0',
                        padding: '14px 18px',
                        fontSize: '0.92rem', color: '#333', lineHeight: '1.7',
                    }}>
                        {feed.ai_summary}
                    </div>
                </div>
            )}

            {/* Content */}
            {showZh || !isHtml ? (
                <div style={{ fontSize: '1rem', lineHeight: '1.8', color: '#4b5563' }}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={readerComponents}>
                        {displayContent}
                    </ReactMarkdown>
                </div>
            ) : (
                <>
                    <div
                        style={{ fontSize: '1.05rem', lineHeight: '1.8', color: '#333', wordBreak: 'break-word' }}
                        dangerouslySetInnerHTML={{ __html: displayContent }}
                        className="article-content"
                    />
                    <style>{`
                        .article-content img { max-width: 100%; height: auto; border-radius: 12px; margin: 1rem 0; }
                        .article-content p { margin: 0.8rem 0; }
                        .article-content h1, .article-content h2, .article-content h3,
                        .article-content h4, .article-content h5, .article-content h6 { color: #1D1D1F; margin: 1.5rem 0 0.5rem; line-height: 1.3; }
                        .article-content h1 { font-size: 1.4rem; }
                        .article-content h2 { font-size: 1.2rem; }
                        .article-content h3 { font-size: 1.1rem; }
                        .article-content a { color: #007AFF; text-decoration: none; }
                        .article-content a:hover { text-decoration: underline; }
                        .article-content blockquote { border-left: 3px solid rgba(0,0,0,0.1); margin: 1rem 0; padding: 0.5rem 1rem; color: #666; background: rgba(0,0,0,0.02); border-radius: 0 8px 8px 0; }
                        .article-content pre { background: rgba(0,0,0,0.04); padding: 1rem; border-radius: 10px; overflow-x: auto; font-size: 0.88rem; line-height: 1.5; }
                        .article-content code { background: rgba(0,0,0,0.04); padding: 2px 6px; border-radius: 4px; font-size: 0.88rem; }
                        .article-content pre code { background: none; padding: 0; }
                        .article-content table { width: 100%; border-collapse: collapse; margin: 1rem 0; font-size: 0.9rem; }
                        .article-content th, .article-content td { padding: 8px 12px; border: 1px solid rgba(0,0,0,0.08); text-align: left; }
                        .article-content th { background: rgba(0,0,0,0.04); font-weight: 600; }
                        .article-content ul, .article-content ol { padding-left: 1.5rem; margin: 0.8rem 0; }
                        .article-content li { margin: 0.3rem 0; }
                        .article-content figure { margin: 1rem 0; text-align: center; }
                        .article-content figcaption { font-size: 0.82rem; color: #86868B; margin-top: 6px; }
                    `}</style>
                </>
            )}

            <div style={{ height: '80px' }} />
        </>
    );

    return (
        <div style={{ maxWidth: hasSidebar ? '1100px' : '800px', margin: '0 auto', padding: '0 32px' }}>
            {/* Back button */}
            <button onClick={goBack} style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                background: 'none', border: 'none', color: '#6366f1',
                fontSize: '0.875rem', fontWeight: '500', cursor: 'pointer',
                padding: '16px 0', marginBottom: '8px',
            }}>
                <ArrowLeftIcon style={{ width: '16px', height: '16px' }} />
                返回
            </button>

            {hasSidebar ? (
                <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>{mainContent}</div>
                    <div style={{ width: '300px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                        {marketImpact?.impact_level && (
                            <div style={{ ...card, padding: '1.25rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                                    <ChartBarIcon style={{ width: '18px', height: '18px', color: '#AF52DE' }} />
                                    <span style={{ fontSize: '0.92rem', fontWeight: '700', color: '#1D1D1F' }}>市场影响</span>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <span style={{ fontSize: '0.82rem', color: '#86868B' }}>影响级别</span>
                                        {badge(marketImpact.impact_level, impactStyle(marketImpact.impact_level))}
                                    </div>
                                    {marketImpact.market_sentiment && (
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <span style={{ fontSize: '0.82rem', color: '#86868B' }}>市场情绪</span>
                                            {badge(marketImpact.market_sentiment, marketSentimentStyle(marketImpact.market_sentiment))}
                                        </div>
                                    )}
                                    {marketImpact.affected_sectors && Array.isArray(marketImpact.affected_sectors) && marketImpact.affected_sectors.length > 0 && (
                                        <div>
                                            <span style={{ fontSize: '0.82rem', color: '#86868B', display: 'block', marginBottom: '6px' }}>相关行业</span>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                                                {marketImpact.affected_sectors.map((sector, i) => (
                                                    <span key={i} style={{
                                                        padding: '3px 10px', borderRadius: '8px', fontSize: '0.75rem',
                                                        fontWeight: '500', background: 'rgba(88,86,214,0.08)', color: '#5856D6',
                                                    }}>{typeof sector === 'string' ? sector : ''}</span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {marketImpact.key_stocks && (
                                        <div>
                                            <span style={{ fontSize: '0.82rem', color: '#86868B', display: 'block', marginBottom: '6px' }}>关键股票</span>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                                                {(Array.isArray(marketImpact.key_stocks)
                                                    ? marketImpact.key_stocks.filter(s => typeof s === 'string')
                                                    : (typeof marketImpact.key_stocks === 'object' ? Object.keys(marketImpact.key_stocks) : [])
                                                ).map((stock, i) => (
                                                    <span key={i} style={{
                                                        padding: '3px 10px', borderRadius: '8px', fontSize: '0.75rem',
                                                        fontWeight: '600', background: 'rgba(0,122,255,0.08)', color: '#007AFF',
                                                    }}>{stock}</span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        {hasMetrics(keyMetrics) && (
                            <div style={{ ...card, padding: '1.25rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                                    <span style={{ fontSize: '0.92rem', fontWeight: '700', color: '#1D1D1F' }}>关键指标</span>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {keyMetrics.revenue && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderRadius: '10px', background: 'rgba(52,199,89,0.06)' }}>
                                            <span style={{ fontSize: '0.82rem', color: '#86868B' }}>营收</span>
                                            <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#1D1D1F' }}>{typeof keyMetrics.revenue === 'object' ? keyMetrics.revenue.value : keyMetrics.revenue}</span>
                                        </div>
                                    )}
                                    {keyMetrics.eps && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderRadius: '10px', background: 'rgba(52,199,89,0.06)' }}>
                                            <span style={{ fontSize: '0.82rem', color: '#86868B' }}>EPS</span>
                                            <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#1D1D1F' }}>{typeof keyMetrics.eps === 'object' ? keyMetrics.eps.value : keyMetrics.eps}</span>
                                        </div>
                                    )}
                                    {keyMetrics.stock_change && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderRadius: '10px', background: String(keyMetrics.stock_change).startsWith('-') ? 'rgba(255,59,48,0.06)' : 'rgba(52,199,89,0.06)' }}>
                                            <span style={{ fontSize: '0.82rem', color: '#86868B' }}>股价变化</span>
                                            <span style={{ fontSize: '0.85rem', fontWeight: '600', color: String(keyMetrics.stock_change).startsWith('-') ? '#FF3B30' : '#34C759' }}>{typeof keyMetrics.stock_change === 'string' || typeof keyMetrics.stock_change === 'number' ? keyMetrics.stock_change : ''}</span>
                                        </div>
                                    )}
                                    {keyMetrics.guidance && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderRadius: '10px', background: 'rgba(88,86,214,0.06)' }}>
                                            <span style={{ fontSize: '0.82rem', color: '#86868B' }}>指引</span>
                                            <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#5856D6' }}>{typeof keyMetrics.guidance === 'object' ? (keyMetrics.guidance.value || JSON.stringify(keyMetrics.guidance)) : keyMetrics.guidance}</span>
                                        </div>
                                    )}
                                    {keyMetrics.key_numbers && Array.isArray(keyMetrics.key_numbers) && keyMetrics.key_numbers.length > 0 && (
                                        <div style={{ marginTop: '4px' }}>
                                            <span style={{ fontSize: '0.78rem', color: '#86868B', display: 'block', marginBottom: '6px' }}>其他关键数据</span>
                                            {keyMetrics.key_numbers.map((item, i) => (
                                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 12px', borderRadius: '8px', background: 'rgba(0,0,0,0.02)', marginBottom: '4px' }}>
                                                    <span style={{ fontSize: '0.8rem', color: '#86868B' }}>{typeof (item.label || item.name) === 'string' ? (item.label || item.name) : ''}</span>
                                                    <span style={{ fontSize: '0.82rem', fontWeight: '600', color: '#1D1D1F' }}>{typeof item.value === 'string' || typeof item.value === 'number' ? item.value : ''}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            ) : mainContent}
        </div>
    );
};

const ArticlePage = ({ mode }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isArticle = mode === 'article';
    const isAIEvent = mode === 'ai-event';
    const [feed, setFeed] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                let res;
                if (isArticle) {
                    res = await apiService.getArticleById(id);
                } else if (isAIEvent) {
                    res = await apiService.getAIEventById(id);
                } else {
                    res = await apiService.getFeedById(id);
                }
                setFeed(res?.data || res);
            } catch (err) {
                console.error('Failed to load:', err);
                setError(err.response?.status === 404 ? 'not_found' : 'error');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id, isArticle, isAIEvent]);

    const articleContent = useMemo(() => {
        if (!feed?.content || !isArticle) return '';
        return feed.content.replace(/^```(?:markdown)?\s*\n?/, '').replace(/\n?```\s*$/, '');
    }, [feed, isArticle]);

    const headings = useMemo(() => {
        if (!articleContent) return [];
        return extractHeadings(articleContent);
    }, [articleContent]);

    const goBack = () => {
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate(isArticle || isAIEvent ? '/' : '/feed');
        }
    };

    // ======== Loading / Error states ========
    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '6rem 0', color: '#9ca3af' }}>
                <ArrowPathIcon style={{ width: '28px', margin: '0 auto 12px', animation: 'spin 1s linear infinite' }} />
                <div style={{ fontSize: '0.95rem' }}>Loading...</div>
            </div>
        );
    }

    if (error === 'not_found') {
        return (
            <div style={{ textAlign: 'center', padding: '6rem 0' }}>
                <div style={{ fontSize: '1.2rem', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>文章未找到</div>
                <div style={{ fontSize: '0.9rem', color: '#9ca3af', marginBottom: '20px' }}>该文章不存在或已被删除</div>
                <button onClick={goBack} style={{
                    padding: '10px 24px', backgroundColor: '#6366f1', color: '#fff',
                    border: 'none', borderRadius: '8px', fontSize: '0.9rem',
                    fontWeight: '500', cursor: 'pointer',
                }}>返回</button>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ textAlign: 'center', padding: '6rem 0' }}>
                <div style={{ fontSize: '1.2rem', fontWeight: '600', color: '#ef4444', marginBottom: '8px' }}>加载失败</div>
                <div style={{ fontSize: '0.9rem', color: '#9ca3af', marginBottom: '20px' }}>出了点问题，请重试</div>
                <button onClick={goBack} style={{
                    padding: '10px 24px', backgroundColor: '#6366f1', color: '#fff',
                    border: 'none', borderRadius: '8px', fontSize: '0.9rem',
                    fontWeight: '500', cursor: 'pointer',
                }}>返回</button>
            </div>
        );
    }

    if (!feed) return null;

    // ======== Article mode: WriteRead-style clean reader ========
    if (isArticle) {
        const styleInfo = STYLE_COLORS[feed.style] || STYLE_COLORS.zhihu;
        return (
            <div style={{ maxWidth: '1100px', margin: '0 auto', display: 'flex', gap: '0' }}>
                <div style={{ flex: 1, minWidth: 0, maxWidth: '800px', margin: '0 auto', padding: '0 32px' }}>
                    <button onClick={goBack} style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        background: 'none', border: 'none', color: '#6366f1',
                        fontSize: '0.875rem', fontWeight: '500', cursor: 'pointer',
                        padding: '16px 0', marginBottom: '8px',
                    }}>
                        <ArrowLeftIcon style={{ width: '16px', height: '16px' }} />
                        返回
                    </button>
                    <h1 style={{
                        fontSize: '2.2rem', fontWeight: '700', letterSpacing: '-0.5px',
                        lineHeight: '1.25', color: '#111827', margin: '0 0 20px',
                    }}>{feed.title}</h1>
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
                        paddingBottom: '24px', marginBottom: '32px', borderBottom: '1px solid #e5e7eb',
                    }}>
                        <span style={{
                            fontSize: '0.8rem', fontWeight: '600', padding: '3px 12px',
                            borderRadius: '6px', background: styleInfo.bg, color: styleInfo.color,
                        }}>{styleInfo.label}</span>
                        {feed.company_symbol && (
                            <span style={{
                                fontSize: '0.8rem', fontWeight: '600', padding: '3px 12px',
                                borderRadius: '6px', background: 'rgba(0,122,255,0.08)', color: '#007AFF',
                            }}>{feed.company_symbol}</span>
                        )}
                        {feed.author && <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>By {feed.author}</span>}
                        {(feed.published_at || feed.created_at) && (
                            <span style={{ fontSize: '0.85rem', color: '#9ca3af' }}>
                                {new Date(feed.published_at || feed.created_at).toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' })}
                            </span>
                        )}
                        {feed.word_count > 0 && <span style={{ fontSize: '0.85rem', color: '#9ca3af' }}>{feed.word_count.toLocaleString()} 字</span>}
                    </div>
                    {feed.tags && feed.tags.length > 0 && (
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '24px', marginTop: '-16px' }}>
                            {feed.tags.map((tag, i) => (
                                <span key={i} style={{
                                    fontSize: '0.75rem', fontWeight: '600', padding: '3px 10px',
                                    borderRadius: '6px', background: 'rgba(88,86,214,0.08)', color: '#5856D6',
                                }}>{typeof tag === 'string' ? tag : ''}</span>
                            ))}
                        </div>
                    )}
                    <div style={{ fontSize: '1rem', lineHeight: '1.8', color: '#4b5563' }}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={readerComponentsWithIds}>{articleContent}</ReactMarkdown>
                    </div>
                    <div style={{ height: '80px' }} />
                </div>
                {headings.length > 0 && (
                    <aside style={{
                        width: '200px', flexShrink: 0, padding: '48px 24px',
                        position: 'sticky', top: '56px', height: 'fit-content',
                        maxHeight: 'calc(100vh - 56px)', overflowY: 'auto',
                    }}>
                        <div style={{
                            fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase',
                            letterSpacing: '0.5px', color: '#9ca3af', marginBottom: '16px',
                        }}>目录</div>
                        {headings.map((h, i) => (
                            <a key={i} href={`#${h.id}`} style={{
                                display: 'block', fontSize: h.level === 3 ? '0.75rem' : '0.8125rem',
                                color: '#9ca3af', textDecoration: 'none',
                                padding: '6px 0', paddingLeft: h.level === 3 ? '12px' : '0',
                                borderLeft: '2px solid transparent', transition: 'color 0.15s', lineHeight: '1.4',
                            }}
                                onMouseEnter={e => { e.target.style.color = '#111827'; }}
                                onMouseLeave={e => { e.target.style.color = '#9ca3af'; }}
                            >{h.text}</a>
                        ))}
                    </aside>
                )}
            </div>
        );
    }

    // ======== Feed & AI Event: unified content view ========
    return <ContentView feed={feed} setFeed={setFeed} goBack={goBack} isAIEvent={isAIEvent} />;
};

export default ArticlePage;
